import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SignupLayout } from "../components/SignupLayout";
import { SignupActivityChoices } from "../components/SignupActivityChoices";
import { LogoutButton } from "../components/LogoutButton";

export default function ChooseActivities() {
  const { user, loading, setUser, logout } = useAuth();
  const [, navigate] = useLocation();
  useEffect(()=>{if(!loading&&user&&new URLSearchParams(window.location.search).get('focus')==='find_work')requestAnimationFrame(()=>{const target=document.getElementById('activity-choice-find_work');target?.scrollIntoView({block:'center'});target?.focus({preventScroll:true})})},[loading,user]);
  const [picked, setPicked] = useState<string[]>(user?.capabilities.length ? user.capabilities : ["find_work"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (user.userType === "admin") { navigate("/admin", { replace: true }); return; }
    setPicked(user.capabilities.length ? user.capabilities : ["find_work"]);
  }, [loading, user, navigate]);

  async function saveActivities() {
    if (busy || !user) return;
    setBusy(true);
    setError(null);
    try {
      // [account.activities](../../../docs/product/accounts.md#account-activities): activities are additive; leaving every choice empty retains Find Work.
      const activities = picked.length ? picked : ["find_work"];
      const missing = activities.filter((activity) => !user.capabilities.includes(activity));
      const updated = missing.length ? await api.addActivities(missing) : user;
      if (!updated) throw new Error("Could not save your activities. Please try again.");
      setUser(updated);
      const status = await api.onboardingStatus().catch(() => ({ needsConsent: true, discoveryPromptPending: true }));
      navigate(status.needsConsent || status.discoveryPromptPending ? "/welcome" : "/dashboard", { replace: true });
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "Could not save your activities. Please try again.");
    } finally { setBusy(false); }
  }

  async function signOut() {
    setBusy(true);
    setError(null);
    try { await logout(); }
    catch (failure: unknown) { setError("Could not sign out. Please try again."); throw failure; }
    finally { setBusy(false); }
  }

  if (loading || !user || user.userType === "admin") return <div className="grid min-h-[100dvh] place-items-center text-sm text-mut" role="status">Loading account setup…</div>;

  return (
    <SignupLayout>
      <h1 id="signup-form-heading" className="text-center text-[26px] font-semibold leading-tight tracking-tight text-navy">What would you like to do?</h1>
      <div className="mt-7" role="group" aria-labelledby="signup-form-heading">
        <SignupActivityChoices picked={picked} busy={busy} onToggle={(activity) => {
          setPicked((current) => current.includes(activity) ? current.filter((key) => key !== activity) : [...current, activity]);
          setError(null);
        }} />
      </div>
      <p className="sr-only" aria-live="polite">{picked.length ? `${picked.length} ${picked.length === 1 ? "activity" : "activities"} selected` : "Find Work is the default."}</p>
      {error && <p className="mt-3 text-sm text-brand-red" role="alert">{error}</p>}
      <button type="button" onClick={saveActivities} disabled={busy} className="btn-red mt-5 min-h-12 w-full rounded-full text-sm font-semibold disabled:opacity-60">{busy ? "Saving activities…" : "Continue"}</button>
      <LogoutButton disabled={busy} onLogout={signOut} variant="ghost" className="mt-5 min-h-11 w-full text-center text-sm font-semibold text-navy hover:underline disabled:opacity-60">Sign out</LogoutButton>
    </SignupLayout>
  );
}
